import { cn, GLASS_INTERACTIVE } from '@/constants/design-tokens';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatPaise } from '@/utils/currency';
import { BookingStatusBadge } from './BookingStatusBadge';
import { formatDeliveryTime, formatBookingDate, depositPercent } from './constants';
import type { BookingListItem } from './types';

interface Props {
  booking: BookingListItem;
  onOpen: (id: string) => void;
}

export function BookingMobileCard({ booking, onOpen }: Props) {
  const currency = useTenantCurrency();
  const pct = depositPercent(booking.advance_amount, booking.total_amount);
  return (
    <button
      type="button"
      onClick={() => onOpen(booking.id)}
      className={cn(
        GLASS_INTERACTIVE,
        'w-full text-left p-4 animate-slide-up focus:outline-none focus:ring-2 focus:ring-brand-500/50',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900 dark:text-neutral-100 truncate">
            {booking.customer_name}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono tabular-nums">
            {booking.customer_phone}
          </p>
        </div>
        <BookingStatusBadge status={booking.status} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-neutral-400 uppercase tracking-wide">Booking</p>
          <p className="font-mono tabular-nums text-neutral-700 dark:text-neutral-200 truncate">
            {booking.booking_number}
          </p>
        </div>
        <div>
          <p className="text-neutral-400 uppercase tracking-wide">Delivery</p>
          <p className="font-mono tabular-nums text-neutral-700 dark:text-neutral-200">
            {formatBookingDate(booking.delivery_date)}
            {booking.delivery_slot ? ` · ${formatDeliveryTime(booking.delivery_slot)}` : ''}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
          {formatPaise(booking.total_amount, currency)}
        </span>
        <span
          className={cn(
            'text-xs px-2 py-0.5 rounded-full font-medium tabular-nums',
            pct >= 100
              ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-200'
              : pct > 0
                ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                : 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-200',
          )}
        >
          {pct}% paid
        </span>
      </div>
    </button>
  );
}
